import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocalStorage } from '@rehooks/local-storage';
import { LOCAL_STORAGE_PREFIX } from 'config';
import { usePage } from 'providers/page';
import React, { ReactNode } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Menu from './Menu';
import { MenuStateProvider } from './MenuStateProvider';
import TopBar from './TopBar';
import { TopLevelMenuItem } from './types';
import { DRAWER_WIDTH, DRAWER_WIDTH_CLOSED } from './utils';

export const CONTENT_WITHOUT_SUB_NAV_MOBILE_OFFSET = 55;
export const CONTENT_WITHOUT_SUB_NAV_OFFSET = 65;

export const CONTENT_WITH_SUB_NAV_MOBILE_OFFSET = 105;
export const CONTENT_WITH_SUB_NAV_OFFSET = 110;

const getMarginTop = (isLarge: boolean, hasSubNavigation: boolean) => {
  if (!hasSubNavigation && !isLarge) return CONTENT_WITHOUT_SUB_NAV_MOBILE_OFFSET;
  if (hasSubNavigation && isLarge) return CONTENT_WITH_SUB_NAV_OFFSET;
  if (hasSubNavigation && !isLarge) return CONTENT_WITH_SUB_NAV_MOBILE_OFFSET;
  return CONTENT_WITHOUT_SUB_NAV_OFFSET;
};

type LayoutProps = {
  menu: TopLevelMenuItem[];
  children: ReactNode;
  toggleLocale: (newLocale: string) => void;
  locale: string;
  menuOpen: boolean,
};

const Layout = ({ children, menu, toggleLocale, locale, menuOpen }: LayoutProps) => {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const [savedOpen, setSavedOpenOpen] = useLocalStorage(`${LOCAL_STORAGE_PREFIX}-MENU`, true);
  const [open, setOpen] = React.useState(false);
  const { state: pageState } = usePage();
  const hasSubNavigation = pageState.subNavigation.length > 0;

  // See: https://github.com/mui-org/material-ui/issues/9337
  const resizeEventDebounced = useDebouncedCallback(
    () => {
      window.dispatchEvent(new CustomEvent('resize'));
    },
    195,
  );

  React.useEffect(() => {
    !isLarge && setOpen(false);
    resizeEventDebounced();
  }, [isLarge, resizeEventDebounced]);

  const handleMenuOpen = () => {
    setOpen(true);
    // See: https://github.com/mui-org/material-ui/issues/9337
    resizeEventDebounced();
    isLarge && setSavedOpenOpen(true);
  };

  const handleMenuClosed = () => {
    setOpen(false);
    // See: https://github.com/mui-org/material-ui/issues/9337
    resizeEventDebounced();
    isLarge && setSavedOpenOpen(false);
  };

  const openBySize = isLarge ? savedOpen : open;
  const isOpen = !menuOpen ? false : openBySize;

  const paddingTop = `${getMarginTop(isLarge, hasSubNavigation)}px`;
  const paddingLeft = `${isOpen ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED}px`;

  return (
    <>
      <TopBar open={isOpen} openMenu={handleMenuOpen} paddingLeft={paddingLeft} toggleLocale={toggleLocale} locale={locale} />
      <Menu menu={menu} closeMenu={handleMenuClosed} open={isOpen} />
      <Box
        sx={{
          poistion: 'absolute',
          left: 0,
          top: 0,
          paddingLeft,
          paddingTop,
        }}
      >
        <MenuStateProvider menuState={isOpen}>
          {children}
        </MenuStateProvider>
      </Box>
    </>
  );
};

export default Layout;
